import React, {useCallback} from 'react';
import {getHelmetForMetaTags} from '../../utils/epicGamesUtils';
import {withLocalization} from '@epic-core/localization';
import styled from 'styled-components';
import {Link, Navigate} from 'react-router-dom';
import {useCmsData, AppSharedKeys, cmsApi, RouteMatchOrUrlParse} from 'epic-ue-shared';
import {generateRoutePath} from '@epic-core/common';
import LoadIndicator from '../../components/common/loading/LoadIndicator';
import {UeThemeProvider, ueLightTheme, ueDarkTheme} from 'epic-ue-theme';
import {
    ArticleContainer,
    ArticleMetaCopy,
    Button,
    GridContainer,
    HeroBasic,
    TagPill,
    VerticalSpacer
} from 'epic-ue-ui';
import Grid from '@material-ui/core/Grid';
import parse from 'url-parse';
import root from 'window-or-global';
import {getQueryStringified} from './jobs.utils';
import {useJob} from './jobs.hooks';
import {jobFetcher} from './jobs.fetch';
import {JobsPageData} from './JobsView';
import {Helmet} from 'react-helmet';
import URLParse from 'url-parse';
import ErrorJobNotFound from '../error/ErrorJobNotFound';
import JobApplicationForm from './form/JobApplicationForm';
import SubmitSuccessMessage from './form/formElements/SubmitSuccessMessage';

const cmsUrlPattern = '/careers/jobs';

const Styled = styled.div`
    min-height: 100vh;
    position: relative;
    overflow: hidden;
    background: ${(p) => p.theme.palette.background.default};

    .social-icon-grid {
        h1,
        .h1,
        h2,
        .h2,
        h3,
        .h3 {
            margin-top: initial;
            margin-bottom: initial;
            font-size: initial;
        }
        svg {
            color: #fff !important;
        }
    }

    .jobDetailsSummary .heading {
        font-weight: bold;
        margin-top: 10px;
    }

    .applyNowButton.secondary {
        width: fit-content;
        border-image: initial;

        .underline.container {
            width: fit-content;
        }
    }

    ${(p) => p.theme.breakpoints.down('xs')} {
        .applyNowButton-wrapper {
            width: 100%;
            text-align: center;
        }
        && .job-form-grid-section {
            padding: 0px;
        }
    }

    .applyNowButton-wrapper {
        a,
        a:hover,
        .bootstrap-custom a:hover,
        .bootstrap-custom a:focus {
            text-decoration: none;
            color: inherit;
        }
    }

    .social-icon-grid svg {
        font-size: 25px;
    }
    .image-wrapper {
        display: flex;
        justify-content: center;
        img {
            max-width: 1000px;
        }
    }
    .image-wrapper img {
        max-width: 1000px;
        margin: 0 auto;
    }
    .hero-img {
        margin: 0 auto;
    }
    .meta-no-image {
        ${(p) => p.theme.breakpoints.down('xs')} {
            font-size: 1.5em;
            text-align: center;
        }
    }

    .copy-wrapper .raw-copy {
        h1 {
            margin-top: 24px;
            margin-bottom: 16px;
            font-size: 40px;
            line-height: 46px;
            letter-spacing: 0;
        }
        h2 {
            font-size: 28px;
            line-height: 32px;
        }
        a {
            text-decoration: none;
        }
    }
    ${(p) => p.theme.breakpoints.down('xs')} {
        .job-application-grid-container {
            padding: 0;
        }
    }

    ${(p) => p.theme.breakpoints.up('md')} {
        .job-application-grid-container .job-form-grid-section {
            padding-left: 48px;
            padding-right: 48px;
        }
    }
`;

const TagPills = styled.div`
    position: relative;
    z-index: 1;
    display: flex;
    flex-wrap: wrap;
    min-height: 32px;
    font-size: 14px;

    .tag-pill {
        margin-top: 5px;
    }

    border-top: 1px solid #6c6c6e;
    padding-top: 25px;

    .tag-pill {
        background-color: ${(p) => p.theme.palette.common.white};
        color: ${(p) => p.theme.palette.common.black};
    }
`;

const getSafeCompanyName = (company: string): string => {
    const safeCompany = company
        ?.replace(/[\s]/, '')
        .toLowerCase()
        .replace('3lateral', 'threeLateral');
    return safeCompany;
};

const JobsDetailView = ({getMessage}: {getMessage: (key: string) => string}): JSX.Element => {
    const {cmsLoading, cmsLoadedEmpty, pageData} = useCmsData<JobsPageData>(cmsUrlPattern);
    const {applyButtonlabel, maintenanceMsg} = pageData;
    const detailImageMapping = pageData.detailImageMapping || {};
    const companyDetailImageMapping = pageData.companyDetailImageMapping || {};
    const defaultImage = detailImageMapping.default;

    const url = parse(root.location.href, true);
    const jobId = url.pathname.substring(url.pathname.lastIndexOf('/') + 1);
    const {job, isLoading} = useJob(jobId);

    const product = job?.product;
    const company = job?.company || '';
    const lowerProductName = product?.replace(/[\s]/, '').toLowerCase();
    const activeImage = detailImageMapping[lowerProductName || ''];
    const activeCompanyImage = companyDetailImageMapping[getSafeCompanyName(company) || ''];

    const [showJobApplicationForm, setShowJobApplicationForm] = React.useState(false);
    const [submitMessage, setSubmitMessage] = React.useState('');

    const stringifiedQueryParams = getQueryStringified();

    const onClickApply = useCallback(
        (e) => {
            e.preventDefault();
            setShowJobApplicationForm(true);
        },
        [setShowJobApplicationForm]
    );

    if (cmsLoading) {
        return (
            <div className="JobDetail">
                {getHelmetForMetaTags(pageData)}
                <LoadIndicator />
            </div>
        );
    }

    if (!job && !isLoading && maintenanceMsg) {
        return <Navigate to={generateRoutePath('/careers/jobs')} />;
    }

    if (cmsLoadedEmpty || (!job && !isLoading)) {
        return <ErrorJobNotFound />;
    }

    const jobDetailsHTMLContent = job?.content
        ?.replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"');

    let jobDetailsMetadata = '';
    const detailTypes = [
        {key: 'Department', label: getMessage('epic.jobs.detail.department')},
        {key: 'Location', label: getMessage('epic.jobs.detail.location')},
        {key: 'Product', label: getMessage('epic.jobs.detail.product')},
        {key: 'Company', label: getMessage('epic.jobs.detail.company')}
    ];
    detailTypes.map(({key: detailType, label}) => {
        if (detailType === 'Location') {
            jobDetailsMetadata += `<strong>${label}</strong><p>`;

            jobDetailsMetadata += `${job?.city
                .map((v, k) => {
                    const str = [job?.country[k]];
                    if (v.toLocaleLowerCase() !== 'blank') {
                        str.unshift(v);
                    }
                    return str.join(', ');
                })
                .join('<br>')}</p>`;
        } else {
            if (job?.[detailType.toLocaleLowerCase()]) {
                jobDetailsMetadata += `<strong>${label}</strong><p>${
                    job?.[detailType.toLocaleLowerCase()]
                }</p>`;
            }
        }
    });

    if (job?.requisition_id) {
        jobDetailsMetadata += `<strong>${getMessage('epic.jobs.detail.requisitionId')}</strong><p>${
            job.requisition_id
        }<br/>`;
    }

    const jobMetaTitleSuffix = getMessage('epic.jobs.detail.metaTitleSuffix');
    const jobHeroTitle = `${job?.title} ${job?.requisition_id && `(${job?.requisition_id})`}`;
    const jobMetaTitle = `${jobHeroTitle} - ${pageData?.header?.title || jobMetaTitleSuffix}`;
    const shareImage = activeCompanyImage || activeImage || defaultImage;

    const tagPillsSection = (
        <TagPills>
            {job?.department ? (
                <Link
                    to={generateRoutePath(
                        `/careers/jobs?page=1&department=${encodeURIComponent(
                            job.department
                        )}&${stringifiedQueryParams}`
                    )}>
                    <TagPill label={job.department} hideX={true} />
                </Link>
            ) : (
                ''
            )}
            {job?.product ? (
                <Link
                    to={generateRoutePath(
                        `/careers/jobs?page=1&product=${encodeURIComponent(
                            job.product
                        )}&${stringifiedQueryParams}`
                    )}>
                    <TagPill label={job.product} hideX={true} />
                </Link>
            ) : (
                ''
            )}
            {job?.company ? (
                <Link
                    to={generateRoutePath(
                        `/careers/jobs?page=1&company=${encodeURIComponent(
                            job.company
                        )}&${stringifiedQueryParams}`
                    )}>
                    <TagPill label={job.company} hideX={true} />
                </Link>
            ) : (
                ''
            )}
        </TagPills>
    );

    return (
        <UeThemeProvider theme={ueLightTheme}>
            <Styled>
                {getHelmetForMetaTags(pageData)}
                {job?.title && (
                    <Helmet
                        meta={[
                            {property: 'og:title', content: jobMetaTitle},
                            {name: 'twitter:title', content: jobMetaTitle},
                            {property: 'og:image', content: shareImage},
                            {name: 'twitter:image', content: shareImage}
                        ]}
                        title={jobMetaTitle}
                    />
                )}
                {(job?.department || job?.title) && (
                    <UeThemeProvider theme={ueDarkTheme}>
                        <HeroBasic
                            eyebrow={{text: job?.department}}
                            title={jobHeroTitle}
                            image={{src: shareImage}}
                        />
                    </UeThemeProvider>
                )}
                <VerticalSpacer variant="section" />

                {job?.content && (
                    <>
                        <GridContainer>
                            <Grid container direction="row" spacing={3}>
                                <VerticalSpacer variant="section" />
                                <ArticleMetaCopy
                                    metaContent={jobDetailsMetadata}
                                    copy={jobDetailsHTMLContent}
                                />
                            </Grid>

                            <GridContainer className="job-application-grid-container">
                                <Grid container direction="row" spacing={2}>
                                    <Grid item xs={12} sm={3} md={2}></Grid>
                                    <Grid item xs={12} sm={9} md={8}>
                                        <ArticleContainer>
                                            <VerticalSpacer variant="section" />

                                            {!showJobApplicationForm &&
                                                (submitMessage ? (
                                                    <>
                                                        {/* Job Application Submit/Thank you Message */}
                                                        <SubmitSuccessMessage />
                                                    </>
                                                ) : (
                                                    <>
                                                        <div className="applyNowButton-wrapper">
                                                            <Button
                                                                className="applyNowButton"
                                                                onClick={onClickApply}
                                                                type="secondary"
                                                                color="white">
                                                                {applyButtonlabel ||
                                                                    getMessage(
                                                                        'epic.jobs.detail.applyButton'
                                                                    )}
                                                            </Button>
                                                        </div>

                                                        <VerticalSpacer variant="heading" />
                                                        {tagPillsSection}
                                                    </>
                                                ))}
                                        </ArticleContainer>
                                    </Grid>
                                    <Grid item xs={12} sm={1} md={2}></Grid>
                                    <Grid className="job-form-grid-section" item xs={12} sm={12}>
                                        {showJobApplicationForm && (
                                            <>
                                                <JobApplicationForm
                                                    key={`jobForm-${jobId}`}
                                                    jobId={jobId}
                                                    jobType={job?.type}
                                                    questions={job?.questions}
                                                    demographicQuestions={
                                                        job?.demographic_questions
                                                    }
                                                    education={job?.education}
                                                    submitMessage={submitMessage}
                                                    setSubmitMessage={setSubmitMessage}
                                                    setShowJobApplicationForm={
                                                        setShowJobApplicationForm
                                                    }
                                                />
                                                <VerticalSpacer variant="heading" />
                                                {tagPillsSection}
                                            </>
                                        )}
                                    </Grid>
                                </Grid>
                            </GridContainer>
                        </GridContainer>
                    </>
                )}
                <VerticalSpacer variant="section" />
            </Styled>
        </UeThemeProvider>
    );
};

export const getJobsDetailViewInitialSharedValues = async (
    match: RouteMatchOrUrlParse,
    url: URLParse<{[key: string]: string | undefined}>
): Promise<any> => {
    const pageData: JobsPageData = await cmsApi.getPageData(cmsUrlPattern);

    const responses: {key: any; value: any}[] = [
        {
            key: AppSharedKeys.CmsStore,
            value: {
                [cmsUrlPattern]: pageData
            }
        }
    ];

    const jobId = url.pathname.substring(url.pathname.lastIndexOf('/') + 1);
    let job;
    try {
        job = await jobFetcher.getJob(jobId);
    } catch (e) {
        job = null;
    }

    responses.push({
        key: AppSharedKeys.JobsStore,
        value: {
            ...job,
            [jobId]: job
        }
    });

    return responses;
};

export default withLocalization(JobsDetailView);
